/** Anything that carries tags — a volunteer or a post. */
export interface TagTarget {
  id: string;
  tagIds: string[];
}

export interface TagDelta {
  add: string[];
  remove: string[];
}

/** Picker mark for a tag across the targets: all have it, some do, or none. */
export function tagMark(targets: readonly TagTarget[], tagId: string): '✓' | '–' | '' {
  const n = targets.filter((t) => t.tagIds.includes(tagId)).length;
  return targets.length > 0 && n === targets.length ? '✓' : n > 0 ? '–' : '';
}

/** A click on a tag in the picker: take it off when every target has it, otherwise give it to all. */
export function toggleDelta(targets: readonly TagTarget[], tagId: string): TagDelta {
  const allHave = targets.length > 0 && targets.every((t) => t.tagIds.includes(tagId));
  return allHave ? { add: [], remove: [tagId] } : { add: [tagId], remove: [] };
}

/** The tag set after a delta, or null when the delta changes nothing on it. */
export function applyDelta(tagIds: readonly string[], d: TagDelta): string[] | null {
  const next = [...new Set([...tagIds, ...d.add])].filter((id) => !d.remove.includes(id));
  const same = next.length === tagIds.length && next.every((id) => tagIds.includes(id));
  return same ? null : next;
}

/** Tag id → how many of the lists carry it. */
export function countUsage(lists: Iterable<readonly string[]>): Map<string, number> {
  const m = new Map<string, number>();
  for (const ids of lists) for (const id of ids) m.set(id, (m.get(id) ?? 0) + 1);
  return m;
}

/** AND filter: the target carries every selected tag. */
export function matchesAll(tagIds: readonly string[], filter: readonly string[]): boolean {
  return filter.every((id) => tagIds.includes(id));
}

/** The filter without tags that no longer exist, so a deleted tag cannot keep a list empty. */
export function liveFilter(filter: readonly string[], known: ReadonlyMap<string, unknown>): string[] {
  return filter.filter((id) => known.has(id));
}

/** The labels of the target's tags joined for a free-text search. */
export function tagSearchText(
  tagIds: readonly string[],
  byId: ReadonlyMap<string, { label: string }>,
): string {
  return tagIds.map((id) => byId.get(id)?.label ?? '').join(' ');
}
